if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      connectedCallback() {
        if (this.listeners) return;
        this.listeners = new AbortController();
        const { signal } = this.listeners;
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.viewer) return;

        // Liquid identifies the first actual product media independently of variant ordering.
        this.heroMediaId = this.dataset.firstProductMediaType === 'video'
          ? this.dataset.firstProductMediaId
          : null;

        this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this), { signal });
        this.elements.thumbnails?.addEventListener('click', (event) => {
          const mediaToSwitch = event.target.closest('button')?.closest('[data-target]');
          if (mediaToSwitch) this.setActiveMedia(mediaToSwitch.dataset.target, false);
        }, { signal });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        if (this.heroMediaId) {
          document.addEventListener('visibilitychange', () => this.syncHeroVideo(), { signal });
          this.refreshHeroVideo();
        }
      }

      disconnectedCallback() {
        this.listeners?.abort();
        this.listeners = null;
        this.heroObserver?.disconnect();
        this.resetHeroVideo();
        this.hero = null;
        window.clearTimeout(this.mediaScrollTimer);
        window.clearTimeout(this.announcementTimer);
      }

      refreshHeroVideo() {
        if (!this.heroMediaId) return;
        const item = this.elements.viewer.querySelector(`[data-media-id="${this.heroMediaId}"]`);
        if (item === this.hero?.item) return;

        this.heroObserver?.disconnect();
        this.hero?.video.pause();
        this.hero = null;

        const video = item?.querySelector('.product__hero-video');
        if (!video) return;

        this.hero = { item, video, visible: false };
        const hero = this.hero;
        this.heroObserver = new IntersectionObserver(([entry]) => {
          if (this.hero !== hero || !this.isConnected) return;
          hero.visible = entry.isIntersecting && entry.intersectionRatio >= 0.15;
          this.syncHeroVideo();
        }, { threshold: [0, 0.15] });
        this.heroObserver.observe(item);
        this.syncHeroVideo();
      }

      resetHeroVideo() {
        this.hero?.video.pause();
      }

      canPlayHeroVideo() {
        return this.isConnected && this.hero?.item.isConnected && this.hero.visible &&
          this.hero.item.classList.contains('is-active') && !document.hidden;
      }

      syncHeroVideo() {
        const hero = this.hero;
        if (!hero) return;

        if (!this.canPlayHeroVideo()) {
          hero.video.pause();
          return;
        }

        hero.video.muted = true;
        const playRequest = hero.video.play();
        if (playRequest !== undefined) {
          playRequest.catch(() => {
            // The native poster remains visible if the browser blocks autoplay.
          });
        }
      }

      onSlideChanged(event) {
        const activeMedia = event.detail.currentElement;
        if (!activeMedia?.dataset.mediaId) return;
        if (!activeMedia.classList.contains('is-active')) {
          this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
            element.classList.toggle('is-active', element === activeMedia);
          });
          this.playActiveMedia(activeMedia);
        }
        const thumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${activeMedia.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.clearTimeout(this.mediaScrollTimer);
        this.mediaScrollTimer = window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          window.clearTimeout(this.announcementTimer);
          this.announcementTimer = window.setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        this.refreshHeroVideo();
        if (activeItem.dataset.mediaId === this.heroMediaId) {
          this.syncHeroVideo();
          return;
        }
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
